import { generatePath } from 'react-router-dom';
import { ExternalLink, LoaderCircle } from 'lucide-react';

import { GetOneDevice } from '@/api/fe-api/view/queries/get-one-device';
import { Link } from '@/components/ui/link';
import { PageUrlKey } from '@/entities/page-url-key';
import { useMe } from '@/hooks/use-me';
import type { Asset } from '@/siteview/entities/asset';
import { GetFiltersQuery } from '@/system-logs/api/queries/get-filters';
import { GetLogCountQuery } from '@/system-logs/api/queries/get-log-count';
import {
  buildSystemLogsFilters,
  buildSystemLogsSearch,
} from '@/system-logs/utils/build-deeplink';
import { SystemLogsInternalIcon } from '@/system-logs/view/components/system-logs-internal-icon';
import { label } from '@/utils/format/label';

export type AssetTooltipSystemLogsProps = {
  siteId: string;
  asset: Asset;
  unitAccureId: string;
};
export function AssetTooltipSystemLogs({
  siteId,
  unitAccureId,
  asset,
}: AssetTooltipSystemLogsProps) {
  const { config } = useMe();
  const prefetchFilters = GetFiltersQuery.usePrefetch();
  const { data: device } = GetOneDevice.useQuery({
    id: unitAccureId,
  });

  const { data: { count: logCount } = { count: 0 }, isLoading } =
    GetLogCountQuery.useQuery({
      siteAccureId: siteId,
      unitAccureId: unitAccureId,
    });

  const pathname = generatePath(
    `/:assetType/:siteId/${PageUrlKey.SYSTEM_LOGS}`,
    {
      assetType: config.type === 'mobility' ? 'vehicle' : 'asset',
      siteId,
    },
  );

  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1 text-sm text-gray-600">
        <SystemLogsInternalIcon featureFlag="systemLogs" />
        Logs
      </span>
      <div className="flex animate-fadeIn items-center gap-2 transition-opacity duration-300">
        {isLoading ? (
          <LoaderCircle className="size-3 animate-spin text-gray-300" />
        ) : (
          typeof logCount === 'number' && (
            <Link
              to={{
                pathname,
                search: buildSystemLogsSearch(asset, device),
              }}
              onMouseEnter={() => {
                // Prefetch filters to make the destination page feel instant
                prefetchFilters({
                  assetId: asset.fullFrontendId,
                  filters: buildSystemLogsFilters(asset, device),
                });
              }}
              className="flex items-center text-sm text-gray-600 underline hover:text-primary-700"
              data-testid="asset-tooltip-system-logs-link"
            >
              <span>
                {logCount.toLocaleString()}{' '}
                {label(['log entry', 'log entries'], logCount)}
              </span>
              <ExternalLink className="ml-1 size-3" />
            </Link>
          )
        )}
      </div>
    </div>
  );
}
